import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Calendar, List, ChevronLeft, ChevronRight, Truck, MapPin, Phone, Package, Clock, Check, Filter, Search, X, Plus, Wrench, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const STATUT_LIVRAISON_COLORS = {
  a_planifier: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'À planifier', border: 'border-gray-200' },
  assignee: { bg: 'bg-orange-50', text: 'text-orange-700', label: 'Assignée', border: 'border-orange-200' },
  en_cours: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'En cours', border: 'border-blue-200' },
  planifiee: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Planifiée', border: 'border-blue-200' },
  livree: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Livrée', border: 'border-emerald-200' }
};

const STATUT_INTERVENTION_COLORS = {
  a_planifier: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'À planifier', border: 'border-gray-200' },
  assignee: { bg: 'bg-orange-50', text: 'text-orange-700', label: 'Assignée', border: 'border-orange-200' },
  en_cours: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'En cours', border: 'border-blue-200' },
  terminee: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Terminée', border: 'border-emerald-200' },
  a_reassigner: { bg: 'bg-red-50', text: 'text-red-600', label: 'À réassigner', border: 'border-red-200' },
};

export default function Livraisons() {
  const [livraisons, setLivraisons] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [stats, setStats] = useState({ aujourdhui: 0, semaine: 0, a_planifier: 0 });
  const [interventionStats, setInterventionStats] = useState({ total: 0, a_planifier: 0, en_cours: 0, aujourdhui: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar');
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [filterStatut, setFilterStatut] = useState('');
  const [filterType, setFilterType] = useState(''); // '', 'livraison', 'intervention'
  const [search, setSearch] = useState('');
  const [showNewIntervention, setShowNewIntervention] = useState(false);
  const navigate = useNavigate();

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  // Format date to YYYY-MM-DD using LOCAL timezone (not UTC)
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Extract just the date part (YYYY-MM-DD) from an ISO string without timezone conversion
  function extractDatePart(dateStr) {
    if (!dateStr) return '';
    // Handle both "2026-03-18T15:10" and "2026-03-18T15:10:00.000Z" formats
    // Just take the first 10 characters (YYYY-MM-DD)
    return dateStr.substring(0, 10);
  }

  function formatDateFR(dateStr) {
    if (!dateStr) return '';
    // Parse date parts directly to avoid timezone issues
    const [datePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
    return `${String(day).padStart(2, '0')} ${months[month - 1]}`;
  }

  function formatHeure(dateStr) {
    if (!dateStr) return '';
    // Extract time directly from the string to avoid timezone conversion
    const timePart = dateStr.split('T')[1];
    if (!timePart) return '';
    // Handle "15:10" or "15:10:00" or "15:10:00.000Z"
    return timePart.substring(0, 5);
  }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const weekEnd = addDays(currentWeekStart, 7);
      const params = {
        date_from: formatDate(currentWeekStart),
        date_to: formatDate(weekEnd),
        statut_livraison: filterStatut || undefined
      };
      const [livraisonsRes, statsRes, interventionsRes, intStatsRes] = await Promise.all([
        api.get('/commandes/livraisons/planning', { params }),
        api.get('/commandes/livraisons/stats'),
        api.get('/interventions/planning', { params: { date_from: formatDate(currentWeekStart), date_to: formatDate(weekEnd) } }),
        api.get('/interventions/stats'),
      ]);
      setLivraisons(livraisonsRes.data.livraisons || []);
      setStats(statsRes.data);
      setInterventions(interventionsRes.data.interventions || []);
      setInterventionStats(intStatsRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, filterStatut]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevWeek = () => setCurrentWeekStart(addDays(currentWeekStart, -7));
  const nextWeek = () => setCurrentWeekStart(addDays(currentWeekStart, 7));
  const goToToday = () => setCurrentWeekStart(getMonday(new Date()));

  const markAsLivree = async (commandeId) => {
    if (!window.confirm('Confirmer la livraison ?\n\nCette action marquera la commande comme livrée avec la date et l\'heure actuelles.')) {
      return;
    }
    try {
      await api.put(`/commandes/${commandeId}/livraison`, null, {
        params: { statut_livraison: 'livree' }
      });
      toast.success('Livraison marquée comme effectuée');
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const annulerLivraison = async (commandeId) => {
    if (!window.confirm('Annuler cette livraison ?\n\nLa commande reviendra au statut "Planifiée".')) {
      return;
    }
    try {
      await api.put(`/commandes/${commandeId}/livraison`, null, {
        params: { statut_livraison: 'planifiee' }
      });
      toast.success('Livraison annulée');
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Group livraisons by day for calendar view
  const livraisonsByDay = {};
  const interventionsByDay = {};
  for (let i = 0; i < 7; i++) {
    const dayDate = formatDate(addDays(currentWeekStart, i));
    livraisonsByDay[dayDate] = (filterType === '' || filterType === 'livraison')
      ? livraisons.filter(l => l.date_livraison_prevue && extractDatePart(l.date_livraison_prevue) === dayDate)
      : [];
    interventionsByDay[dayDate] = (filterType === '' || filterType === 'intervention')
      ? interventions.filter(iv => iv.date_prevue && iv.date_prevue.substring(0, 10) === dayDate)
      : [];
  }

  // Filter for list view
  const allItems = [
    ...((filterType === '' || filterType === 'livraison') ? livraisons.map(l => ({ ...l, _type: 'livraison' })) : []),
    ...((filterType === '' || filterType === 'intervention') ? interventions.map(iv => ({ ...iv, _type: 'intervention' })) : []),
  ];
  const filteredItems = allItems.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (item.client_societe || '').toLowerCase().includes(s) ||
      (item.client_adresse || '').toLowerCase().includes(s) ||
      (item.titre || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Planning</h1>
          <p className="text-sm text-gray-500 mt-1">Livraisons et interventions SAV</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNewIntervention(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
            data-testid="new-intervention-btn"
          >
            <Plus size={16} />
            Intervention
          </button>
          <button
            onClick={() => setView('calendar')}
            className={`p-2 rounded-lg transition-colors ${view === 'calendar' ? 'bg-[hsl(222,47%,11%)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid="view-calendar-btn"
          >
            <Calendar size={18} />
          </button>
          <button
            onClick={() => setView('list')}
            className={`p-2 rounded-lg transition-colors ${view === 'list' ? 'bg-[hsl(222,47%,11%)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid="view-list-btn"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Truck size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.aujourdhui}</p>
              <p className="text-xs text-gray-500">Livraisons aujourd'hui</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Calendar size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.semaine}</p>
              <p className="text-xs text-gray-500">Livraisons semaine</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Wrench size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{interventionStats.en_cours + interventionStats.a_planifier}</p>
              <p className="text-xs text-gray-500">Interventions actives</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
              <Clock size={20} className="text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.a_planifier}</p>
              <p className="text-xs text-gray-500">À planifier</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400" />
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]" data-testid="filter-type">
            <option value="">Tout afficher</option>
            <option value="livraison">Livraisons</option>
            <option value="intervention">Interventions</option>
          </select>
          <select
            value={filterStatut}
            onChange={e => setFilterStatut(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
            data-testid="filter-statut"
          >
            <option value="">Tous les statuts</option>
            <option value="a_planifier">À planifier</option>
            <option value="planifiee">Planifiée</option>
            <option value="livree">Livrée</option>
          </select>
        </div>
        {view === 'list' && (
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
            />
          </div>
        )}
      </div>

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <button onClick={prevWeek} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-gray-900">
                Semaine du {currentWeekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
              </h2>
              <button
                onClick={goToToday}
                className="text-xs px-2 py-1 bg-[hsl(222,47%,11%)] text-white rounded hover:bg-[hsl(222,47%,18%)]"
              >
                Aujourd'hui
              </button>
            </div>
            <button onClick={nextWeek} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 divide-x divide-gray-100">
            {/* Day Headers */}
            {JOURS.map((jour, idx) => {
              const dayDate = addDays(currentWeekStart, idx);
              const isToday = formatDate(dayDate) === formatDate(new Date());
              return (
                <div key={jour} className={`px-2 py-3 text-center border-b border-gray-100 ${isToday ? 'bg-blue-50' : ''}`}>
                  <p className="text-xs text-gray-500">{jour}</p>
                  <p className={`text-lg font-semibold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                    {dayDate.getDate()}
                  </p>
                </div>
              );
            })}

            {/* Day Content */}
            {JOURS.map((_, idx) => {
              const dayDate = addDays(currentWeekStart, idx);
              const dayStr = formatDate(dayDate);
              const dayLivraisons = livraisonsByDay[dayStr] || [];
              const isToday = dayStr === formatDate(new Date());

              return (
                <div key={dayStr} className={`min-h-[200px] p-2 ${isToday ? 'bg-blue-50/30' : ''}`}>
                  <div className="space-y-2">
                    {dayLivraisons.map(liv => {
                      const statutConfig = STATUT_LIVRAISON_COLORS[liv.statut_livraison] || STATUT_LIVRAISON_COLORS.a_planifier;
                      return (
                        <div
                          key={liv.id}
                          onClick={() => navigate(`/commandes/${liv.id}`)}
                          className={`p-2 rounded-lg border cursor-pointer hover:shadow-md transition-all ${statutConfig.bg} border-${statutConfig.text.replace('text-', '')}/30`}
                          data-testid={`livraison-card-${liv.id}`}
                        >
                          <p className="text-xs font-medium text-gray-600">
                            {formatHeure(liv.date_livraison_prevue)}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {liv.client_societe}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <Package size={10} className="text-gray-400" />
                            <p className="text-xs text-gray-500 truncate">
                              {liv.lignes?.map(l => l.reference).join(', ') || liv.modele}
                            </p>
                          </div>
                          {liv.statut_livraison !== 'livree' ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); markAsLivree(liv.id); }}
                              className="mt-2 w-full text-xs px-2 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 flex items-center justify-center gap-1"
                            >
                              <Check size={12} /> Livré
                            </button>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); annulerLivraison(liv.id); }}
                              className="mt-2 w-full text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 flex items-center justify-center gap-1"
                            >
                              <X size={12} /> Annuler
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {/* Interventions SAV */}
                    {(interventionsByDay[dayStr] || []).map(iv => {
                      const ivCfg = STATUT_INTERVENTION_COLORS[iv.statut] || STATUT_INTERVENTION_COLORS.a_planifier;
                      return (
                      <div
                        key={iv.id}
                        onClick={() => navigate(`/interventions/${iv.id}`)}
                        className={`p-2 rounded-lg border cursor-pointer hover:shadow-md transition-all ${ivCfg.bg} ${ivCfg.border}`}
                        data-testid={`intervention-card-${iv.id}`}
                      >
                        <div className="flex items-center gap-1 text-xs">
                          <Wrench size={10} className={ivCfg.text} />
                          <span className={`font-medium ${ivCfg.text}`}>
                            {iv.type?.toUpperCase() || 'SAV'}
                          </span>
                          {iv.heure_debut && <span className="text-gray-500 ml-auto">{iv.heure_debut}</span>}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">{iv.client_societe}</p>
                        <p className="text-xs text-gray-500 truncate">{iv.titre}</p>
                        <span className={`mt-1 inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${ivCfg.bg} ${ivCfg.text}`}>{ivCfg.label}</span>
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Détail</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Adresse</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    Aucun élément trouvé
                  </td>
                </tr>
              ) : (
                filteredItems.map(item => {
                  const isInterv = item._type === 'intervention';
                  const statutConfig = isInterv
                    ? (STATUT_INTERVENTION_COLORS[item.statut] || STATUT_INTERVENTION_COLORS.a_planifier)
                    : (STATUT_LIVRAISON_COLORS[item.statut_livraison] || STATUT_LIVRAISON_COLORS.a_planifier);

                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(isInterv ? `/interventions/${item.id}` : `/commandes/${item.id}`)}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${isInterv ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                          {isInterv ? <Wrench size={10} /> : <Truck size={10} />}
                          {isInterv ? 'SAV' : 'Livraison'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {isInterv ? formatDateFR(item.date_prevue) : formatDateFR(item.date_livraison_prevue)} {isInterv && item.heure_debut ? ` ${item.heure_debut}` : !isInterv ? ` ${formatHeure(item.date_livraison_prevue)}` : ''}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{item.client_societe}</p>
                        {item.client_telephone && (
                          <p className="text-xs text-gray-400 flex items-center gap-1"><Phone size={10} /> {item.client_telephone}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {isInterv ? item.titre : (item.lignes?.map(l => `${l.reference} x${l.quantite}`).join(', ') || item.modele)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={10} /> {item.client_adresse}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statutConfig.bg} ${statutConfig.text}`}>{statutConfig.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        {isInterv ? (
                          <button onClick={e => { e.stopPropagation(); navigate(`/interventions/${item.id}`); }} className="text-xs px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-1">
                            <Wrench size={12} /> Détail
                          </button>
                        ) : item.statut_livraison !== 'livree' ? (
                          <button onClick={e => { e.stopPropagation(); markAsLivree(item.id); }} className="text-xs px-3 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 flex items-center gap-1">
                            <Check size={12} /> Livré
                          </button>
                        ) : (
                          <button onClick={e => { e.stopPropagation(); annulerLivraison(item.id); }} className="text-xs px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 flex items-center gap-1">
                            <X size={12} /> Annuler
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Nouvelle Intervention */}
      {showNewIntervention && <NewInterventionModal onClose={() => setShowNewIntervention(false)} onCreated={() => { setShowNewIntervention(false); fetchData(); }} />}
    </div>
  );
}

function NewInterventionModal({ onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [livreurs, setLivreurs] = useState([]);
  const [searchClient, setSearchClient] = useState('');
  const [form, setForm] = useState({ client_id: '', type: 'sav', titre: '', description: '', date_prevue: '', heure_debut: '', heure_fin: '', technicien_id: '', technicien_nom: '', priorite: 'normale' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/clients'), api.get('/users/livreurs/list')]).then(([cRes, lRes]) => {
      const cData = Array.isArray(cRes.data) ? cRes.data : cRes.data.clients || [];
      setClients(cData);
      setLivreurs(lRes.data || []);
    });
  }, []);

  const filteredClients = clients.filter(c =>
    (c.societe || '').toLowerCase().includes(searchClient.toLowerCase())
  ).slice(0, 10);

  const selectClient = (c) => { setForm(f => ({ ...f, client_id: c.id })); setSearchClient(c.societe); };

  const selectLivreur = (e) => {
    const l = livreurs.find(l => l.id === e.target.value);
    setForm(f => ({ ...f, technicien_id: l?.id || '', technicien_nom: l ? `${l.prenom || ''} ${l.nom || ''}`.trim() : '' }));
  };

  const handleSubmit = async () => {
    if (!form.client_id) { toast.error('Sélectionnez un client'); return; }
    if (!form.titre.trim()) { toast.error('Ajoutez un titre'); return; }
    setSaving(true);
    try {
      await api.post('/interventions', form);
      toast.success('Intervention créée');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="new-intervention-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-[hsl(222,47%,11%)] flex items-center gap-2"><Wrench size={18} className="text-orange-500" /> Nouvelle intervention</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          {/* Client search */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Client *</label>
            <div className="relative">
              <input value={searchClient} onChange={e => { setSearchClient(e.target.value); setForm(f => ({ ...f, client_id: '' })); }} placeholder="Rechercher un client..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="client-search-input" />
              {searchClient && !form.client_id && filteredClients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredClients.map(c => (
                    <button key={c.id} onClick={() => selectClient(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0">
                      {c.societe}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="type-select">
                <option value="sav">SAV</option>
                <option value="maintenance">Maintenance</option>
                <option value="visite">Visite</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Priorité</label>
              <select value={form.priorite} onChange={e => setForm(f => ({ ...f, priorite: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="priorite-select">
                <option value="normale">Normale</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Titre *</label>
            <input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} placeholder="Ex: Fauteuil KLOSS - bruit moteur" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="titre-input" />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description du problème..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none h-20" data-testid="description-input" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Date</label>
              <input type="date" value={form.date_prevue} onChange={e => setForm(f => ({ ...f, date_prevue: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="date-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Début</label>
              <input type="time" value={form.heure_debut} onChange={e => setForm(f => ({ ...f, heure_debut: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="heure-debut-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Fin</label>
              <input type="time" value={form.heure_fin} onChange={e => setForm(f => ({ ...f, heure_fin: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="heure-fin-input" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Technicien</label>
            <select value={form.technicien_id} onChange={selectLivreur} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="technicien-select">
              <option value="">— Non assigné —</option>
              {livreurs.map(l => (
                <option key={l.id} value={l.id}>{l.prenom} {l.nom}</option>
              ))}
            </select>
          </div>

          <button onClick={handleSubmit} disabled={saving} className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium text-sm hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-intervention-btn">
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
            Créer l'intervention
          </button>
        </div>
      </div>
    </div>
  );
}
